import type { Client } from '@microsoft/microsoft-graph-client';
import { ResponseType } from '@microsoft/microsoft-graph-client';
import type { DriveItem, GraphCollection } from './client';

/**
 * Base path for the Impact Agent app folder in OneDrive
 * Uses the special approot folder which is scoped to the registered app
 */
const APP_ROOT = '/me/drive/special/approot:/progress-agent';

// Logger - disabled to reduce noise (enable for debugging)
const DEBUG_GRAPH = false;
const log = (message: string, data?: unknown) => {
  if (!DEBUG_GRAPH) return;
  const timestamp = new Date().toISOString().split('T')[1];
  console.log(`[${timestamp}] 📁 Graph: ${message}`, data ?? '');
};

/**
 * Read a JSON file from the app folder
 */
export async function readJson<T>(graph: Client, relativePath: string): Promise<T | null> {
  log(`Reading JSON: ${relativePath}`);
  try {
    const response = await graph
      .api(`${APP_ROOT}/${relativePath}:/content`)
      .get();
    
    log(`Read JSON success: ${relativePath}`);
    // Response might be a string or already parsed
    if (typeof response === 'string') {
      return JSON.parse(response) as T;
    }
    return response as T;
  } catch (error: unknown) {
    const graphError = error as { statusCode?: number };
    // 404 means file doesn't exist - return null instead of throwing
    if (graphError.statusCode === 404) {
      log(`File not found (404): ${relativePath}`);
      return null;
    }
    log(`Read JSON error: ${relativePath}`, error);
    throw error;
  }
}

/**
 * Write a JSON file to the app folder
 */
export async function writeJson(
  graph: Client, 
  relativePath: string, 
  data: unknown
): Promise<DriveItem> {
  log(`Writing JSON: ${relativePath}`);
  const content = JSON.stringify(data, null, 2);
  
  const response = await graph
    .api(`${APP_ROOT}/${relativePath}:/content`)
    .put(content);
  
  log(`Write JSON success: ${relativePath}`);
  return response as DriveItem;
}

/**
 * Read raw file content (for markdown files)
 */
export async function readFileContent(graph: Client, relativePath: string): Promise<string | null> {
  log(`Reading file content: ${relativePath}`);
  try {
    const response = await graph
      .api(`${APP_ROOT}/${relativePath}:/content`)
      .responseType(ResponseType.TEXT)
      .get();
    
    log(`Read file success: ${relativePath} (type: ${typeof response}, length: ${typeof response === 'string' ? response.length : 'N/A'})`);
    
    // Handle various response types
    if (typeof response === 'string') {
      return response;
    }
    
    if (response instanceof Blob) {
      log(`Converting Blob response to text for ${relativePath}`);
      return await response.text();
    }
    
    if (response instanceof ArrayBuffer) {
      log(`Converting ArrayBuffer response to text for ${relativePath}`);
      return new TextDecoder().decode(response);
    }
    
    // Fallback
    log(`Unexpected response type for ${relativePath}:`, typeof response);
    return String(response);
  } catch (error: unknown) {
    const graphError = error as { statusCode?: number };
    if (graphError.statusCode === 404) {
      log(`File not found (404): ${relativePath}`);
      return null;
    }
    log(`Read file error: ${relativePath}`, error);
    throw error;
  }
}

/**
 * Write raw file content
 */
export async function writeFileContent(
  graph: Client,
  relativePath: string,
  content: string
): Promise<DriveItem> {
  const response = await graph
    .api(`${APP_ROOT}/${relativePath}:/content`)
    .put(content);
  
  return response as DriveItem;
}

/**
 * List files in a folder within the app folder
 */
export async function listFolder(
  graph: Client, 
  relativePath: string
): Promise<DriveItem[]> {
  log(`Listing folder: ${relativePath}`);
  try {
    const response: GraphCollection<DriveItem> = await graph
      .api(`${APP_ROOT}/${relativePath}:/children`)
      .get();
    
    log(`Listed folder: ${relativePath} (${response.value.length} items)`);
    return response.value;
  } catch (error: unknown) {
    const graphError = error as { statusCode?: number };
    if (graphError.statusCode === 404) {
      log(`Folder not found (404): ${relativePath}`);
      return [];
    }
    log(`List folder error: ${relativePath}`, error);
    throw error;
  }
}

/**
 * Ensure a folder exists in the app folder, creating it if necessary
 * Returns the folder's drive item ID
 */
export async function ensureFolder(
  graph: Client, 
  relativePath: string
): Promise<string> {
  const segments = relativePath.split('/').filter(Boolean);
  let currentPath = '';
  let lastId = '';

  for (const segment of segments) {
    const parentPath = currentPath ? `${APP_ROOT}/${currentPath}` : APP_ROOT;
    currentPath = currentPath ? `${currentPath}/${segment}` : segment;

    try {
      // Try to get the folder
      const existing = await graph
        .api(`${APP_ROOT}/${currentPath}`)
        .get();
      lastId = existing.id;
    } catch (error: unknown) {
      const graphError = error as { statusCode?: number };
      if (graphError.statusCode === 404) {
        // Create the folder
        const created = await graph
          .api(`${parentPath}:/children`)
          .post({
            name: segment,
            folder: {},
            '@microsoft.graph.conflictBehavior': 'fail',
          });
        lastId = created.id;
      } else {
        throw error;
      }
    }
  }

  return lastId;
}

/**
 * Delete a file or folder from the app folder
 */
export async function deleteItem(
  graph: Client, 
  relativePath: string
): Promise<void> {
  try {
    await graph
      .api(`${APP_ROOT}/${relativePath}`)
      .delete();
  } catch (error: unknown) {
    const graphError = error as { statusCode?: number };
    // Ignore 404 - already deleted
    if (graphError.statusCode !== 404) {
      throw error;
    }
  }
}

/**
 * Get metadata for a file or folder
 */
export async function getItemMetadata(
  graph: Client, 
  relativePath: string
): Promise<DriveItem | null> {
  try {
    const response = await graph
      .api(`${APP_ROOT}/${relativePath}`)
      .get();
    return response as DriveItem;
  } catch (error: unknown) {
    const graphError = error as { statusCode?: number };
    if (graphError.statusCode === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Check if a file or folder exists
 */
export async function itemExists(
  graph: Client, 
  relativePath: string
): Promise<boolean> {
  const item = await getItemMetadata(graph, relativePath);
  return item !== null;
}

/**
 * Append content to a JSONL file (for activity logs)
 */
export async function appendJsonl(
  graph: Client,
  relativePath: string,
  entry: unknown
): Promise<void> {
  const existing = await readFileContent(graph, relativePath) ?? '';
  const newContent = existing + JSON.stringify(entry) + '\n';
  await writeFileContent(graph, relativePath, newContent);
}

/**
 * Read a JSONL file and parse each line
 */
export async function readJsonl<T>(
  graph: Client,
  relativePath: string
): Promise<T[]> {
  const content = await readFileContent(graph, relativePath);
  if (!content) return [];

  return content
    .trim()
    .split('\n')
    .filter(line => line.trim())
    .map(line => JSON.parse(line) as T);
}



